// components/PlaceViewDialog.js
import React, { useEffect, useState } from "react";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from "@mui/icons-material/Place"; // 아이콘 예시
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { supabase } from "../supabaseClient";
import dayjs from "dayjs";

const PlaceViewDialog = ({ open, onClose, placeId }) => {
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageBaseUrl = process.env.REACT_APP_SUPABASE_IMAGE_BASE;
  const fullImageUrl = place?.image_url
  ? `${imageBaseUrl}${place?.image_url.replace(/^\/?/, "")}`  // 슬래시 제거
  : null;

  // 🔍 Supabase에서 장소 정보 가져오기
  useEffect(() => {
    const fetchPlace = async () => {
      if (!placeId) return;
      setLoading(true);

      try {
        console.log(placeId);

        const { data: placeData, error: placeError } = await supabase
          .from("places")
          .select("*")
          .eq("id", placeId)
          .single();

        const { data: locData } = await supabase
          .from("places_locations")
          .select("lat, lng")
          .eq("place_id", placeId)
          .maybeSingle();

        if (placeError) throw new Error(placeError.message);

        const { data: infoData } = await supabase
          .from("places_info")
          .select("*")
          .eq("place_id", placeId)
          .maybeSingle(); // 없을 수도 있으므로 maybeSingle() 사용

        // ✅ statusLabel 계산
        let statusLabel = null;
        if (infoData?.start_day && infoData?.end_day) {
          const start = dayjs(infoData.start_day);
          const end = dayjs(infoData.end_day);
          const today = dayjs();

          if (today.isBefore(start)) {
            statusLabel = "예정";
          } else if (today.isAfter(end)) {
            statusLabel = "종료";
          } else {
            statusLabel = "진행중";
          }
        }

        setPlace({
          ...placeData,
          lat: locData?.lat || null,
          lng: locData?.lng || null,
          info: infoData || null, // 또는 필요한 속성만 추출해서 구조화해도 OK
          statusLabel
        });


      } catch (err) {
        console.error("장소 정보를 불러올 수 없습니다:", err.message);
        alert("장소 정보를 불러올 수 없습니다.");
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchPlace();
    }
  }, [placeId, open]);

  const renderServiceInfo = (services) => {
    if (!services) return null;

    const {
      close_day,
      weekday_oper_open,
      weekday_oper_close,
      holiday_oper_open,
      holiday_oper_close,
    } = services;

    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
        🕒 <strong>운영시간:</strong>
        {"\n"}- 평일: {weekday_oper_open || "정보 없음"} ~ {weekday_oper_close || "정보 없음"}
        {"\n"}- 주말/공휴일: {holiday_oper_open || "정보 없음"} ~ {holiday_oper_close || "정보 없음"}
        {"\n"}- 휴무일: {close_day || "없음"}
      </Typography>
    );
  };

  const handleNaverMapClick = (address) => {
    const encoded = encodeURIComponent(address);
    const appUrl = `nmap://search?query=${encoded}`;
    const webUrl = `https://map.naver.com/v5/search/${encoded}`;
  
    // 앱으로 먼저 시도, 일정 시간 후 실패 시 웹으로 fallback
    window.location.href = appUrl;
  
    setTimeout(() => {
      // 앱으로 이동하지 못했으면 웹으로 fallback
      window.open(webUrl, "_blank");
    }, 1000); // 앱 전환까지 기다리는 시간
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {loading ? (
        <Box sx={{ p: 5, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : place ? (
        <>
          <DialogTitle sx={{lineHeight:1.1}}>{place.title || "장소 정보"}</DialogTitle>
          <DialogContent dividers>

            {/* 이미지 */}
            {fullImageUrl && (
              <Box sx={{ mb: 2, textAlign: "center", background:"#EEE", borderRadius: 8 }}>
                {!imageLoaded && (
                  <Box sx={{ height: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress size={32} />
                    {/* 또는: <Skeleton variant="rectangular" width="100%" height={200} /> */}
                  </Box>
                )}
                <img
                  onLoad={() => setImageLoaded(true)}
                  src={fullImageUrl}
                  alt={place.title || "장소 이미지"}
                  onError={() => setImageLoaded(true)}
                  style={{ maxWidth: "100%", borderRadius: 8, display: imageLoaded ? "block" : "none" }}
                />
              </Box>
            )}
            
            {/* 주소 */}
            {place.address && (
              <>
                <Typography variant="body2" gutterBottom>
                  <strong>주소:</strong> {place.address}
                </Typography>
                
                <Box>
                {place.lat && place.lng && (
                  <Typography variant="caption" color="text.secondary">
                    좌표: {place.lat}, {place.lng}
                  </Typography>
                )}
                </Box>
                <Divider sx={{ my: 1 }} />
              </>
            )}

            <Typography variant="body">
              {place?.info?.services && renderServiceInfo(place.info.services)}
            </Typography>

            <Typography variant="body">
              {place?.info?.start_day && place?.info?.end_day && (
                <>
                  {dayjs(place.info.start_day).format("YYYY-MM-DD")} ~{" "}
                  {dayjs(place.info.end_day).format("YYYY-MM-DD")}
                  {"  "}
                  <strong>({place.statusLabel})</strong>
                </>
              )}
            </Typography>

            {/* 설명 */}
            {place.content && (
              <Typography variant="body2" sx={{ mt:1, mb: 2 }}>
                {place.content}
              </Typography>
            )}
            
            {/* 나이 */}
            {place.age_min && place.age_max && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>이용 나이:</strong>  {place.age_min} ~ {place.age_max}
              </Typography>
            )}

            {/* 상태 정보 */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
              {place.use === true && <Chip label="영업중" color="primary" />}
              {place.is_reservation === true && <Chip label="예약필수" color="secondary" />}
              {place.is_parking === true && <Chip label="주차가능" color="success" />}
              {place.price == null ? (
                <Chip label="무료" color="info" />
              ) : (
                <Chip label="유료" />
              )}
            </Box>

            {place.phone && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                ☎️ <strong>전화번호:</strong> {place.phone}
              </Typography>
            )}
            
            {place.price && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>가격:</strong>{" "}
                {(() => {
                  if (typeof place.price === "string") {
                    try {
                      const parsed = JSON.parse(place.price);
                      if (typeof parsed === "object" && !Array.isArray(parsed)) {
                        return Object.entries(parsed)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(", ");
                      } else if (Array.isArray(parsed)) {
                        return parsed.join(" / ");
                      }
                    } catch {
                      return place.price;
                    }
                  } else if (typeof place.price === "object") {
                    if (Array.isArray(place.price)) {
                      return place.price.join(", ");
                    } else {
                      return Object.entries(place.price)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(" / ");
                    }
                  }
                  return place.price;
                })()}
              </Typography>
            )}

            <Box sx={{display:"flex",gap:0.5}} dividers>
              <Button variant="outlined" onClick={()=>handleNaverMapClick(place.address)} startIcon={<PlaceIcon />} sx={{flex:1,backgroundColor:"#fff"}}>지도보기</Button>
              
              {place.link && (
              <Button variant="outlined" onClick={() => { if (place.link) window.location.href = place.link; }} startIcon={<CalendarMonthIcon />} sx={{flex:1,backgroundColor:"#fff"}}>현황보기</Button>
              )}
            </Box>

          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>닫기</Button>
          </DialogActions>
        </>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography color="error">장소 데이터를 불러올 수 없습니다.</Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default PlaceViewDialog;
